import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import cookie from 'js-cookie';

/**
 * TextViewer Component
 * Fetches and displays text file content with syntax-friendly formatting.
 *
 * @param {Object} props
 * @param {string} props.documentUrl - Authenticated URL to the text document
 * @param {string} props.filename - Name of the text file
 * @returns {React.ReactElement} Text viewer
 */
const TextViewer = ({ documentUrl, filename }) => {
    const theme = useTheme();
    const [textContent, setTextContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchTextContent = async () => {
            try {
                const response = await axios.get(documentUrl, {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                    responseType: 'text',
                });

                if (isMounted) {
                    setTextContent(response.data);
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Error loading text file:', err);
                    setError('Failed to load text file. Please try downloading it instead.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (documentUrl) {
            fetchTextContent();
        }

        return () => {
            isMounted = false;
        };
    }, [documentUrl]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: theme.palette.grey[50],
                    gap: 2,
                }}
            >
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    Loading text file...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: { xs: 2, sm: 3 },
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Alert
                    severity="error"
                    sx={{
                        maxWidth: 600,
                        width: '100%',
                        boxShadow: theme.shadows[2],
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Unable to Load Text File
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: theme.palette.grey[50],
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', md: '90%', lg: '85%' },
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: { xs: 'none', md: theme.shadows[3] },
                    my: { xs: 0, md: 2.5 },
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: { xs: 0, md: 1 },
                }}
            >
                <pre
                    style={{
                        fontFamily: '"Roboto Mono", Monaco, "Courier New", monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.7,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        color: theme.palette.text.primary,
                        overflow: 'auto',
                    }}
                >
                    {textContent}
                </pre>
            </Box>
        </Box>
    );
};

TextViewer.propTypes = {
    documentUrl: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
};

export default TextViewer;
